import { useMutation, useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import Form from '@dashboard/components/Formik/Form';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { fetcher } from '@dashboard/services/request';
import SettingsContainer from '@dashboard/components/SettingsContainer';
import SettingsHeader from '@dashboard/components/SettingsHeader';
import { useToast } from '@dashboard/context/ToastContext';
import { getBillingPeriod } from '@dashboard/services/plans';
import { sellerKey } from '@dashboard/lib/queryKeys';

const CancelSubscription = () => {
  const { seller, plan, sellerId, token, setSeller } = useAuth();
  const [shouldPollSeller, setShouldPollSeller] = useState(false);
  const { emitToast } = useToast();

  const history = useHistory();

  const renewsOn = seller.stripeSubscriptionCurrentPeriodEnd
    ? format(new Date(seller.stripeSubscriptionCurrentPeriodEnd), 'd MMM, yyyy')
    : null;

  const { data: updatedSeller } = useQuery({
    queryKey: sellerKey(sellerId),
    queryFn: () => fetcher(`/sellers/${sellerId}`, token),
    enabled: shouldPollSeller,
    refetchInterval: 1000,
  });

  const cancelSubscriptionMutation = useMutation({
    mutationFn: ({ cancelReason }) =>
      fetcher(`/sellers/${sellerId}/cancel-subscription`, token, {
        method: 'POST',
        body: JSON.stringify({ cancelReason }),
      }),
    onSuccess: () => {
      emitToast({
        title: 'Subscription canceled',
        description: 'Your subscription has been canceled',
      });
      setShouldPollSeller(true);
    },
  });

  useEffect(() => {
    if (updatedSeller?.stripeSubscriptionCancelAt) {
      setSeller(updatedSeller);

      history.push('/billing/plan');
    }
  }, [updatedSeller]);

  return (
    <SettingsContainer>
      <SettingsHeader>Cancel subscription</SettingsHeader>

      <h3 className="text-xl font-normal">
        {plan.title} - {getBillingPeriod(plan.billingPeriod)}{' '}
        {renewsOn && `(renews on ${renewsOn})`}
      </h3>

      <div className="my-4">
        <Form
          showState={false}
          initialValues={{
            cancelReason: '',
          }}
          fields={[
            {
              name: 'cancelReason',
              label: 'Let us know why you’re leaving',
              placeholder: 'Your feedback',
              type: 'textarea',
              required: true,
            },
          ]}
          submitButton={{
            label: 'Cancel subscription',
            appearance: 'light',
            size: 'base',
            disabled: shouldPollSeller,
          }}
          cancelButton={{
            label: 'Back',
            appearance: 'link',
            size: 'base',
            onClick: () => history.push('/billing/plan'),
          }}
          onSubmit={async (values, { setSubmitting, setStatus }) => {
            setSubmitting(true);

            try {
              await cancelSubscriptionMutation.mutateAsync(values);
            } catch (err) {
              setStatus(err.message || 'Something went wrong');
            }

            setSubmitting(false);
          }}
        />
      </div>

      <div>
        If you cancel your subscription, your subscription will remain active until the end of your
        billing period
        {renewsOn ? <strong> ({renewsOn})</strong> : ''}.
      </div>
    </SettingsContainer>
  );
};

export default CancelSubscription;
